import type { MiniAction } from '@otto-ec/tracking-bct';
import { log } from '@shared/log.browser.ts';
import type { TrackingGateway } from '@shared/tracking/Tracking.browser.ts';
import { isSponsoredProduct } from '@shared/types/types.ts';
import type { BlackFridaySliderPort } from '@tilelist/application/ports/black-friday-slider/BlackFridaySliderPort.ts';
import type { SelectionEvents } from '@tilelist/application/ports/SelectionEvents.ts';
import { COLLAPSED_BLACK_FRIDAY_SLIDER } from '@tilelist/domain/black-friday-slider/BlackFridaySlider.ts';
import {
    BLACK_FRIDAY_SLIDER_FEATURE_ID,
    blackFridaySliderClickLabels,
    blackFridaySliderFeatures,
    blackFridaySliderLoadedLabels,
    sliderTileFeatureId,
} from '@tilelist/domain/black-friday-slider/BlackFridaySliderTracking.ts';
import type {
    BlackFridaySliderItem,
    ProductItem,
    ReadyBlackFridaySliderItem,
} from '@tilelist/domain/composition/TileListitem.ts';
import { productClickLabels } from '@tilelist/services/tracking/ProductClickLabels.browser.ts';

export class BlackFridaySliderService implements BlackFridaySliderPort {
    private reportedTilePosition = 0;
    private reportedSlider = false;

    constructor(
        private readonly loader: Pick<BlackFridaySliderPort, 'load'>,
        private readonly gateway: TrackingGateway,
        private readonly selectionEvents: Pick<SelectionEvents, 'current'>,
    ) {}

    async load(blackFridayRule: string | undefined): Promise<BlackFridaySliderItem> {
        let slider: BlackFridaySliderItem;
        try {
            slider = await this.loader.load(blackFridayRule);
        } catch {
            slider = COLLAPSED_BLACK_FRIDAY_SLIDER;
        }
        this.reset();
        this.gateway.addToPageImpression(blackFridaySliderLoadedLabels(slider), blackFridaySliderFeatures(slider));
        return slider;
    }

    startPageImpression(): void {
        this.reset();
    }

    tilesShown(tiles: ProductItem[], slider: ReadyBlackFridaySliderItem): void {
        const miniActions = [...this.unreportedSliderActions(), ...this.unreportedTileActions(tiles, slider)];
        if (miniActions.length === 0) return;

        log.debug(`trackBlackFridaySlider -- miniActions ${JSON.stringify(miniActions)}`);
        this.gateway.submitMiniActions(miniActions);
    }

    tileSeen(item: ProductItem, slider: ReadyBlackFridaySliderItem): void {
        const position = item.localListPosition;
        const tracked = slider.trackedSponsoredViews ?? [];
        if (!isSponsoredProduct(item.product) || tracked.includes(position)) return;

        this.gateway.submitMiniActions([
            { featureId: sliderTileFeatureId(position), action: 'scroll', status: 'visible' },
        ]);
        slider.trackedSponsoredViews = [...tracked, position];
    }

    clicked(event: Event, item: ProductItem, rule: string): void {
        this.gateway.submitMiniActions([
            { featureId: BLACK_FRIDAY_SLIDER_FEATURE_ID, action: 'click', status: 'clicked' },
        ]);

        const trackingData = productClickLabels(event, item, rule, this.selectionEvents.current().entryRule);
        if (!trackingData) return;

        this.gateway.trackOnNextPageImpression(blackFridaySliderClickLabels(trackingData, item));
    }

    private reset(): void {
        this.reportedTilePosition = 0;
        this.reportedSlider = false;
    }

    private unreportedSliderActions(): MiniAction[] {
        if (this.reportedSlider) return [];

        this.reportedSlider = true;
        return [{ featureId: BLACK_FRIDAY_SLIDER_FEATURE_ID, action: 'scroll', status: 'visible' }];
    }

    private unreportedTileActions(tiles: ProductItem[], slider: ReadyBlackFridaySliderItem): MiniAction[] {
        const lastShownPosition = Math.max(0, ...tiles.map(({ localListPosition }) => localListPosition));
        const sponsoredPositions = new Set(
            slider.items
                .filter(({ product }) => isSponsoredProduct(product))
                .map(({ localListPosition }) => localListPosition),
        );
        const actions: MiniAction[] = [];
        while (this.reportedTilePosition < lastShownPosition) {
            this.reportedTilePosition += 1;
            /*                                                                                       */
            if (sponsoredPositions.has(this.reportedTilePosition)) continue;
            actions.push({
                featureId: sliderTileFeatureId(this.reportedTilePosition),
                action: 'scroll',
                status: 'visible',
            });
        }
        return actions;
    }
}
